import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Loader, Popover } from '@mantine/core';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { IoNotificationsOutline } from 'react-icons/io5';
import {
  adminNotificationsApi,
  type AdminNotificationItem,
} from '@modules/admin-notifications/api/admin-notifications.api';
import './AdminNotificationsBell.css';

const UNREAD_QK = ['admin-notifications', 'unread-count'] as const;
const LIST_QK = ['admin-notifications', 'list'] as const;

function orderIdFromData(data: unknown): number | null {
  if (data == null || typeof data !== 'object') return null;
  const raw = (data as { orderId?: unknown }).orderId;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function NotificationRow({
  item,
  onRead,
  onOpen,
}: {
  item: AdminNotificationItem;
  onRead: (id: number) => void;
  onOpen: (item: AdminNotificationItem) => void;
}) {
  const unread = item.status === 'UNREAD';
  return (
    <button
      type="button"
      className={`admin-notifications-bell__row ${unread ? 'admin-notifications-bell__row--unread' : ''}`}
      onClick={() => {
        if (unread) onRead(item.id);
        onOpen(item);
      }}
    >
      <div className="admin-notifications-bell__row-top">
        <span
          className={`admin-notifications-bell__title ${unread ? 'admin-notifications-bell__title--unread' : ''}`}
        >
          {item.title}
        </span>
        {unread ? <span className="admin-notifications-bell__dot" aria-hidden /> : null}
      </div>
      {item.body ? <p className="admin-notifications-bell__body">{item.body}</p> : null}
      <time className="admin-notifications-bell__time" dateTime={item.createdAt}>
        {dayjs(item.createdAt).locale('ru').format('DD MMM YYYY')}
      </time>
    </button>
  );
}

export function AdminNotificationsBell({ enabled }: { enabled: boolean }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);

  const { data: unreadData } = useQuery({
    queryKey: UNREAD_QK,
    queryFn: () => adminNotificationsApi.getUnreadCount(),
    enabled,
    refetchInterval: enabled ? 60_000 : false,
  });
  const unreadCount = unreadData?.count ?? 0;

  const {
    data: listData,
    isLoading: listLoading,
    isFetching,
  } = useQuery({
    queryKey: LIST_QK,
    queryFn: () => adminNotificationsApi.list({ limit: 40 }),
    enabled: enabled && opened,
  });

  const markReadMut = useMutation({
    mutationFn: (id: number) => adminNotificationsApi.markRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UNREAD_QK });
      void queryClient.invalidateQueries({ queryKey: LIST_QK });
    },
  });

  const markAllMut = useMutation({
    mutationFn: () => adminNotificationsApi.markAllRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UNREAD_QK });
      void queryClient.invalidateQueries({ queryKey: LIST_QK });
    },
  });

  const items = listData?.items ?? [];
  const showLoader = listLoading || (isFetching && items.length === 0);

  if (!enabled) return null;

  return (
    <div className="admin-notifications-bell__target-wrap">
      <Popover
        position="bottom-end"
        withArrow={false}
        shadow="none"
        opened={opened}
        onChange={setOpened}
        closeOnClickOutside
        withinPortal
        trapFocus={false}
        zIndex={1200}
        offset={8}
      >
        <Popover.Target>
          <button
            type="button"
            aria-label="Уведомления"
            aria-expanded={opened}
            className={[
              'admin-notifications-bell__trigger',
              opened ? 'admin-notifications-bell__trigger--open' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setOpened((o) => !o)}
          >
            <IoNotificationsOutline />
          </button>
        </Popover.Target>
        <Popover.Dropdown className="admin-notifications-bell__dropdown">
          <div className="admin-notifications-bell__dropdown-header">
            <h2 className="admin-notifications-bell__dropdown-title">Уведомления</h2>
            {unreadCount > 0 ? (
              <button
                type="button"
                className="admin-notifications-bell__mark-all"
                disabled={markAllMut.isPending}
                onClick={() => markAllMut.mutate()}
              >
                {markAllMut.isPending ? '…' : 'Прочитать все'}
              </button>
            ) : null}
          </div>
          {showLoader ? (
            <div className="admin-notifications-bell__state">
              <Loader size="sm" color="var(--accent)" />
            </div>
          ) : items.length === 0 ? (
            <div className="admin-notifications-bell__empty">
              <p className="admin-notifications-bell__empty-title">Нет уведомлений</p>
              <p className="admin-notifications-bell__empty-text">
                Здесь появятся новости по заказам и чатам.
              </p>
            </div>
          ) : (
            <div className="admin-notifications-bell__scroll">
              <div className="admin-notifications-bell__list">
                {items.map((item) => (
                  <NotificationRow
                    key={item.id}
                    item={item}
                    onRead={(id) => markReadMut.mutate(id)}
                    onOpen={(n) => {
                      const orderId = orderIdFromData(n.data);
                      setOpened(false);
                      if (orderId != null) {
                        navigate(`/orders/${orderId}`);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </Popover.Dropdown>
      </Popover>
      {unreadCount > 0 ? (
        <span className="admin-notifications-bell__badge" aria-hidden>
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      ) : null}
    </div>
  );
}
